import jobBoardApi from '../../api/job-board';
import { CRITERIA_VERSION } from '../constants/criteria';
import { fromCriteriaPayload } from './useCriteria';

/**
 * The signed-out half of the Build-Your-Own-Algorithm surface (EN-856).
 *
 * A visitor has neither of the two things a signed-in seeker gets for free: no `features`
 * map, because `Feature::all()` ships only on the user resource, and no `job_board_settings`
 * row to save an algorithm into. So the flag state and the default stack are fetched from
 * the public endpoint, and an edited set lives in localStorage until they sign up.
 *
 * @see docs/architecture/job-board/criteria-contract.md
 */

export const GUEST_CRITERIA_STORAGE_KEY = 'jobBoardGuestCriteria';

/**
 * Whether the criteria surface is live for this visitor, and the default algorithm to open
 * it with.
 *
 * A failure resolves to "off" rather than rejecting: the panel is an enhancement, and a
 * visitor whose request was blocked or timed out should get the board, not a broken pitch.
 *
 * @returns {Promise<{enabled: boolean, criteria: Array<{key: string, userBucket: string}>|null}>}
 */
export async function fetchGuestCriteria() {
	try {
		const data = await jobBoardApi.getDefaultCriteria();

		return {
			enabled: data?.enabled === true,
			criteria: data?.criteria ? fromCriteriaPayload(data) : null,
		};
	} catch {
		return { enabled: false, criteria: null };
	}
}

/**
 * The visitor's own set, or null when they have not edited one this browser remembers.
 *
 * The wire payload is what is stored, so the version travels with it: a set written under an
 * older contract is discarded rather than rehydrated into a shape the engine has since
 * redefined. Values are ignored on the way back — they come from the live filter bar.
 *
 * @returns {Array<{key: string, userBucket: string}>|null}
 */
export function readStoredCriteria() {
	try {
		const stored = JSON.parse(localStorage.getItem(GUEST_CRITERIA_STORAGE_KEY));

		if (stored?.version !== CRITERIA_VERSION || !Array.isArray(stored?.criteria)) {
			return null;
		}

		return fromCriteriaPayload(stored);
	} catch {
		// Unreadable or unparseable — treat it as never having been written.
		return null;
	}
}

/**
 * Persist the visitor's edited algorithm.
 *
 * Best-effort, mirroring the store's `persistSearchFilters()`: localStorage can be
 * unavailable in private mode or over quota, and losing the set on reload is a much smaller
 * failure than losing the search.
 *
 * Survives sign-up: `adoptGuestCriteria()` is what consumes it, on the first authenticated
 * board mount after the visitor registers.
 *
 * @see ./useSavedCriteria.js
 * @param {{version: number, criteria: Array<object>}} payload the wire payload from `toCriteriaPayload()`
 */
export function writeStoredCriteria(payload) {
	try {
		localStorage.setItem(GUEST_CRITERIA_STORAGE_KEY, JSON.stringify(payload));
	} catch {
		// Persistence is best-effort — the in-memory set still drives this session.
	}
}

/**
 * Drop the visitor's local set, once it has been carried into their saved settings.
 */
export function clearStoredCriteria() {
	try {
		localStorage.removeItem(GUEST_CRITERIA_STORAGE_KEY);
	} catch {
		// Same best-effort contract as the write.
	}
}
